import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * TREATX-SOFT-LAUNCH-GATE-01 + TREATX-SOFT-LAUNCH-INVITE-KEYS-01
 *
 * Wraps the entire app. Three bypass paths:
 *   1. Gate disabled in Atlas → everyone in.
 *   2. Authenticated user (any RBAC role) → in.
 *   3. Anonymous holding a valid invite-key session → in.
 *
 * FAIL-CLOSED on any network/config failure: render the gate.
 *
 * The invite-key session token (HMAC-signed, 8h TTL) is stored in
 * localStorage and attached to every subsequent fetch via a global
 * `window.fetch` override at the top of the gate, so any app HTTP call
 * automatically picks up the bypass header without component-level
 * changes.
 */
const API = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const LS_TOKEN_KEY = 'treatx.softLaunch.sessionToken';
const LS_EXP_KEY = 'treatx.softLaunch.sessionExpiresAt';
const SESSION_HEADER = 'X-Soft-Launch-Session';

const readStoredSession = () => {
  try {
    const tok = localStorage.getItem(LS_TOKEN_KEY);
    const exp = localStorage.getItem(LS_EXP_KEY);
    if (!tok || !exp) return null;
    if (new Date(exp).getTime() < Date.now()) return null;
    return { token: tok, expires_at: exp };
  } catch {
    return null;
  }
};

const storeSession = (token, expires_at) => {
  try {
    localStorage.setItem(LS_TOKEN_KEY, token);
    localStorage.setItem(LS_EXP_KEY, expires_at);
  } catch { /* ignore */ }
};

const clearSession = () => {
  try {
    localStorage.removeItem(LS_TOKEN_KEY);
    localStorage.removeItem(LS_EXP_KEY);
  } catch { /* ignore */ }
};

// Install a one-time global fetch wrapper that injects the invite
// session header into every request. Idempotent.
const installFetchInterceptor = () => {
  if (typeof window === 'undefined') return;
  if (window.__treatxFetchInterceptorInstalled) return;
  const orig = window.fetch.bind(window);
  // FIND-H-08 — helper to read the readable CSRF cookie for
  // double-submit attachment on state-changing requests.
  const readCsrf = () => {
    try {
      const raw = typeof document !== 'undefined' ? (document.cookie || '') : '';
      const m = raw.split('; ').find((c) => c.startsWith('treatx_csrf='));
      return m ? decodeURIComponent(m.substring('treatx_csrf='.length)) : '';
    } catch { return ''; }
  };
  const CSRF_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);
  window.fetch = (input, init = {}) => {
    try {
      // Determine whether this call targets our backend API. The
      // credentials + CSRF augmentation MUST only apply to TreatX API
      // calls — third-party fetch (e.g. analytics, Google, Apple)
      // must remain untouched.
      const urlStr = typeof input === 'string'
        ? input
        : (input && input.url) || '';
      const isApiCall = urlStr.startsWith('/api/')
        || (typeof API === 'string' && API && urlStr.startsWith(API))
        || (typeof window !== 'undefined' && window.location
            && urlStr.startsWith(window.location.origin + '/api/'));

      const sess = readStoredSession();
      const headers = new Headers(init.headers || {});
      if (sess && !headers.has(SESSION_HEADER)) headers.set(SESSION_HEADER, sess.token);

      // FIND-H-08 — for TreatX API calls, force credentials:'include'
      // so the browser attaches the HttpOnly `treatx_auth` cookie,
      // and attach `X-CSRF-Token` on state-changing methods (double-
      // submit pattern; Owner directive D2). No manual Authorization
      // header is ever emitted from browser code post-cutover.
      if (isApiCall) {
        // Preserve explicit caller intent (e.g. 'omit' for
        // /api/soft-launch-gate/status). Only default to 'include'
        // when the caller did not specify.
        if (init.credentials == null) {
          init = { ...init, credentials: 'include' };
        }
        const method = (init.method || 'GET').toUpperCase();
        if (CSRF_METHODS.has(method) && !headers.has('X-CSRF-Token')) {
          const csrf = readCsrf();
          if (csrf) headers.set('X-CSRF-Token', csrf);
        }
      }
      init = { ...init, headers };
    } catch { /* ignore */ }
    return orig(input, init);
  };
  window.__treatxFetchInterceptorInstalled = true;
};

const GATE_FETCH_RETRIES = 2;
const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

const fetchGateOnce = async () => {
  const r = await fetch(`${API}/api/soft-launch-gate/status`, { credentials: 'omit' });
  if (!r.ok) {
    const err = new Error(`gate_http_${r.status}`);
    err.httpStatus = r.status;
    throw err;
  }
  const data = await r.json();
  return {
    enabled: data.enabled !== false,
    message: data.message || 'TreatX is in soft launch.',
    estimated_launch_date: data.estimated_launch_date || null,
    // Authoritative read of the canonical setting. Older backends omit
    // the field — absence is treated as authoritative (previous
    // behaviour), a fail-closed payload sets it to false explicitly.
    resolved: data.resolved !== false,
  };
};

/**
 * P0-LAUNCH-GATE-CONFIG-MISMATCH-01
 *
 * A single transient failure (e.g. the backend pod 502-ing for a few
 * seconds during a deployment, while the static bundle is already being
 * served) used to flip the ENTIRE public site to the invitation-only
 * landing, even though the canonical setting said OFF. Remediation:
 *   - bounded retry before concluding the read failed;
 *   - if the canonical state was ALREADY resolved authoritatively in this
 *     page session, keep it — an unreachable config read does not revoke
 *     an authoritative answer;
 *   - FAIL-CLOSED is preserved whenever nothing was ever resolved, and an
 *     authoritative `enabled:true` always re-renders the gate.
 */
const fetchGate = async (previous = null) => {
  let lastErr = null;
  let lastUnresolved = null;
  for (let attempt = 0; attempt <= GATE_FETCH_RETRIES; attempt += 1) {
    try {
      const read = await fetchGateOnce();
      // SOFTLAUNCH-OFF-SURVIVES-DEPLOY-01 — a 200 response can still be a
      // FAIL-CLOSED payload (`resolved:false`): the backend answers
      // `enabled:true` when it cannot read the canonical setting, e.g. the
      // first seconds after a deployment while the database connection is
      // still being established. That is NOT an authoritative gate ON, so it
      // is retried like a transport failure instead of being trusted on
      // sight. Previously it was accepted immediately, which turned a
      // momentary read failure into an invitation-only public site while the
      // persisted setting said OFF.
      if (read.resolved) return read;
      lastUnresolved = read;
      if (attempt < GATE_FETCH_RETRIES) await sleep(400 * (attempt + 1));
    } catch (e) {
      lastErr = e;
      if (attempt < GATE_FETCH_RETRIES) await sleep(400 * (attempt + 1));
    }
  }
  if (previous && previous.resolved) return previous;
  // Nothing authoritative was ever read → FAIL CLOSED, keeping whatever the
  // server said about itself so the reason stays visible to the operator.
  if (lastUnresolved) return lastUnresolved;
  const status = lastErr && lastErr.httpStatus;
  return {
    enabled: true,
    message: status
      ? `Soft launch gate is enforced. Configuration temporarily unavailable (HTTP ${status}).`
      : 'Soft launch gate is enforced. Network error contacting the platform.',
    estimated_launch_date: null,
    resolved: false,
  };
};

const claimInviteCode = async (code) => {
  const r = await fetch(`${API}/api/soft-launch-gate/claim`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!r.ok) {
    const detail = await r.json().catch(() => ({}));
    throw new Error(detail.detail || 'invalid_or_expired_code');
  }
  return r.json();
};

export const SoftLaunchGate = ({ children }) => {
  const { user, isAuthenticated } = useAuth() || {};
  const [gate, setGate] = useState(null);
  const [hasSession, setHasSession] = useState(() => !!readStoredSession());
  const [routePath, setRoutePath] = useState(() => {
    if (typeof window === 'undefined') return '/';
    // Apple's redirect lands on `https://…/auth/apple/callback#id_token=…`
    // — a REAL pathname (not a HashRouter route). Check the real
    // pathname first, then fall back to the hash-router route.
    const pn = (window.location.pathname || '/').split('?')[0];
    if (pn && pn !== '/' && pn !== '/index.html') return pn;
    return (window.location.hash || '').replace(/^#/, '').split('?')[0] || '/';
  });

  useEffect(() => {
    installFetchInterceptor();
    let mounted = true;
    let latest = null;
    let timer = null;

    // SOFTLAUNCH-OFF-SURVIVES-DEPLOY-01 — the real production trigger was a
    // ~100s cold-start window after a deploy where the pod served traffic
    // before uvicorn accepted connections, so /api/* answered 502. With a
    // flat 60s poll the public site LATCHED the invitation-only screen for up
    // to a minute after the backend recovered, which is what made a manual
    // ON/OFF toggle look like the fix. While nothing authoritative has been
    // read we now re-ask quickly (3s, backing off to 15s); once the canonical
    // setting is known we return to the ordinary 60s refresh.
    const schedule = (unresolvedStreak) => {
      const delay = unresolvedStreak
        ? Math.min(3_000 * unresolvedStreak, 15_000)
        : 60_000;
      timer = setTimeout(async () => {
        const g = await fetchGate(latest);
        latest = g;
        if (!mounted) return;
        setGate(g);
        schedule(g.resolved ? 0 : unresolvedStreak + 1);
      }, delay);
    };

    fetchGate(null).then((g) => {
      latest = g;
      if (!mounted) return;
      setGate(g);
      schedule(g.resolved ? 0 : 1);
    });
    return () => { mounted = false; if (timer) clearTimeout(timer); };
  }, []);

  // Track hash route so the gate can allowlist /login etc. The gate
  // wraps the HashRouter, so it doesn't get React Router context — we
  // listen to the native hashchange event directly.
  // TREATX-SEO-RECOVERY-S2: the public surface mounts BrowserRouter, where
  // in-app navigation fires popstate and never hashchange. Both are observed
  // so the gate's route never goes stale on either surface.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onRouteChange = () => {
      const pn = (window.location.pathname || '/').split('?')[0];
      if (pn && pn !== '/' && pn !== '/index.html') {
        setRoutePath(pn);
        return;
      }
      const p = (window.location.hash || '').replace(/^#/, '').split('?')[0] || '/';
      setRoutePath(p);
    };
    window.addEventListener('hashchange', onRouteChange);
    window.addEventListener('popstate', onRouteChange);
    return () => {
      window.removeEventListener('hashchange', onRouteChange);
      window.removeEventListener('popstate', onRouteChange);
    };
  }, []);

  // Poll session expiry every 60s — drop expired sessions.
  useEffect(() => {
    const id = setInterval(() => {
      const sess = readStoredSession();
      if (!sess && hasSession) {
        clearSession();
        setHasSession(false);
      }
    }, 60_000);
    return () => clearInterval(id);
  }, [hasSession]);

  const onClaimSuccess = useCallback(({ session_token, expires_at }) => {
    storeSession(session_token, expires_at);
    setHasSession(true);
  }, []);

  if (gate === null) return null;
  if (!gate.enabled) return children;
  if (isAuthenticated || user) return children;
  if (hasSession) return children;

  // Allowlist anonymous access to auth routes so users can actually
  // reach the login / password reset surfaces from the gate.
  // Mirrors the backend gate allowlist for /api/auth/*.
  if (
    routePath === '/login' ||
    routePath === '/forgot-password' ||
    routePath === '/reset-password' ||
    // TREATX-PROD-UAT-GATE-MFA-01 (2026-08): the mandatory MFA
    // enrollment + challenge surfaces are AUTHENTICATION SECURITY
    // INFRASTRUCTURE required by an already-primary-authenticated
    // privileged user to complete login. They live outside the full
    // session (the intermediate `mfa_enroll_bootstrap` /
    // `mfa_challenge` JWT is held in sessionStorage only — no
    // session cookie is set until the second factor is verified),
    // so `isAuthenticated` above is intentionally false at this
    // point. Without this allowlist the client-side gate replaces
    // the MFA page with the "TreatX is coming soon" landing and
    // privileged login is impossible in production.
    //
    // Exposure is safe: both `MfaEnrollPage` and `MfaChallengePage`
    // gate their own load on a valid `treatx.mfa.token` in
    // sessionStorage and immediately `navigate('/login')` when the
    // token is missing (see `pages/MfaEnrollPage.jsx` L33-42 and
    // `pages/MfaChallengePage.jsx` L27-35). The backend `/api/mfa/*`
    // endpoints independently verify the token's HMAC-SHA256
    // signature against `JWT_SECRET` and its `purpose` claim; an
    // anonymous visitor with no token receives 401 from the API
    // and is redirected to `/login`.
    routePath === '/mfa/enroll' ||
    routePath === '/mfa/challenge' ||
    // TREATX-P0-ONBOARDING-STATE-MACHINE-REDESIGN — verification-link
    // landing page. The user clicks the email link on ANY device (or
    // in a new browser tab), so the gate must let them past without
    // an invite session. The backend allowlists the underlying API
    // (/api/onboarding/verify-email) via the same-named rule.
    routePath === '/onboarding/verify-email' ||
    // TREATX-ADMIN-ONBOARDING-01 — platform-admin first-login flow.
    // The email link lands on `/admin/onboarding/verify-email` in
    // any browser. Backend allowlists the underlying POST via the
    // same-named rule.
    routePath === '/admin/onboarding/verify-email' ||
    routePath === '/admin/onboarding/change-password' ||
    routePath === '/admin/onboarding/verify-required' ||
    // TREATX-APPLE-MOBILE-CALLBACK-BLANK-SCREEN-01 — Apple redirects
    // the user to this URL after Sign in with Apple. The gate MUST
    // let it through; otherwise the invite landing page swallows
    // Apple's id_token in the URL fragment before AppleCallback.jsx
    // can read it.
    routePath === '/auth/apple/callback' ||
    // TREATX-LEGAL-PRODUCTION-READINESS-01 — Public legal surfaces MUST
    // remain accessible during soft launch for iyzico onboarding, Apple
    // App Review, Google Play Review, and GDPR/KVKK transparency.
    // These routes render only Agreement-Library content — no product
    // features — so exposing them does not open the platform.
    routePath === '/terms' ||
    routePath === '/privacy' ||
    routePath === '/privacy-policy' ||
    routePath === '/cookie-policy' ||
    routePath === '/cookie-settings' ||
    routePath === '/refund-policy' ||
    routePath === '/health-tourism-info' ||
    routePath === '/open-source' ||
    routePath === '/contact' ||
    // TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 — public self-reactivation surface.
    // A voluntarily deactivated provider is anonymous (logged out on
    // deactivation) and must reach this page while the soft-launch gate is
    // ON to request the registered-email OTP and restore access.
    routePath === '/reactivate'
  ) {
    return children;
  }

  // FAIL-CLOSED but HONEST: when the canonical setting could not be read
  // (cold start, 502, unresolved payload) we must not render the public site,
  // and we must not claim the platform is invitation-only either. Show a
  // neutral connecting state; the fast retry above replaces it with the real
  // answer within seconds, with no manual toggle. This sits AFTER the
  // allowlist so login, MFA and the legal surfaces stay reachable in exactly
  // the window where someone is most likely to need them.
  if (gate.resolved === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6"
           data-testid="soft-launch-gate-connecting">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-teal-600" />
          <p className="mt-4 text-sm font-medium text-slate-700">
            Connecting to TreatX…
          </p>
          <p className="mt-1 text-xs text-slate-500" data-testid="soft-launch-gate-connecting-detail">
            {gate.message}
          </p>
        </div>
      </div>
    );
  }

  return <SoftLaunchLanding gate={gate} onClaimSuccess={onClaimSuccess} />;
};

const SoftLaunchLanding = ({ gate, onClaimSuccess }) => {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showCode, setShowCode] = useState(false);

  const message = gate.message || 'TreatX is preparing for soft launch. Public access opens soon.';
  const eta = gate.estimated_launch_date;

  // TREATX-OWNER-ACCEPTANCE-P0-INVITE-CLAIM-NORMALIZE-01 (client guard) —
  // The owner has previously pasted an internal Reference ID (starts
  // with `ILK-`) into this input, expecting it to be a claim code.
  // We short-circuit that class of mistake here with a specific,
  // actionable message so ops isn't paged for a false "gate broken"
  // incident. Canonical claim codes are exactly 22 chars in the shape
  // `TX-XXXX-XXXX-XXXX-XXXX`.
  const clientSideRejection = (raw) => {
    const trimmed = (raw || '').trim();
    if (!trimmed) return null;
    if (/^ILK[-_]/i.test(trimmed)) {
      return 'This looks like an internal Reference ID (starts with "ILK-"), not a Claim code. Ask your admin for the "TX-XXXX-XXXX-XXXX-XXXX" claim code shown once at creation.';
    }
    if (!/^TX-/i.test(trimmed)) {
      return 'Claim codes start with "TX-" and are 22 characters long (e.g. TX-XXXX-XXXX-XXXX-XXXX). Please re-check the code from your invitation email.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const guard = clientSideRejection(code);
    if (guard) {
      setError(guard);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await claimInviteCode(code.trim());
      onClaimSuccess(r);
      window.location.hash = '#/';
      window.location.reload();
    } catch (err) {
      setError('That invite code is invalid, expired, or has been used up.');
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="soft-launch-gate" style={landingStyle}>
      <div style={cardStyle}>
        <div style={badgeStyle} data-testid="soft-launch-gate-badge">Soft Launch · Invitation Only</div>
        <h1 style={headlineStyle} data-testid="soft-launch-gate-headline">TreatX is coming soon.</h1>
        <p style={messageStyle} data-testid="soft-launch-gate-message">{message}</p>
        {eta && (
          <p style={etaStyle} data-testid="soft-launch-gate-eta">
            Expected public launch: <strong style={{ color: '#cdd9e8' }}>{eta}</strong>
          </p>
        )}

        <a
          href="#/login"
          data-testid="soft-launch-gate-signin-btn"
          style={primaryLinkBtnStyle}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#2563eb'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#3b82f6'; }}
        >
          Sign in (owner, clinic admin, provider)
        </a>

        <div style={{ margin: '32px 0 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ fontSize: 12, color: '#586577', letterSpacing: '0.16em', textTransform: 'uppercase' }}>or</span>
          <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {!showCode ? (
          <button
            type="button"
            onClick={() => setShowCode(true)}
            data-testid="soft-launch-gate-show-invite-btn"
            style={secondaryBtnStyle}
          >
            I have an invite code
          </button>
        ) : (
          <form onSubmit={handleSubmit} data-testid="soft-launch-gate-invite-form" style={{ marginTop: 4 }}>
            <input
              type="text"
              data-testid="soft-launch-gate-invite-input"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="TX-XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              spellCheck={false}
              maxLength={32}
              style={inputStyle}
              disabled={submitting}
            />
            <button
              type="submit"
              data-testid="soft-launch-gate-invite-submit"
              disabled={submitting || code.trim().length < 4}
              style={{ ...primaryBtnStyle, marginTop: 14, opacity: submitting ? 0.6 : 1 }}
            >
              {submitting ? 'Verifying…' : 'Enter preview'}
            </button>
            {error && (
              <p data-testid="soft-launch-gate-invite-error" style={{ marginTop: 12, color: '#ff8a80', fontSize: 13 }}>
                {error}
              </p>
            )}
          </form>
        )}

        <p style={{ marginTop: 28, fontSize: 12, color: '#586577' }}>
          Press · Compliance · MoH inquiries:&nbsp;
          <a href="mailto:support@treatx.health" style={{ color: '#8cb3ff', textDecoration: 'none' }}>support@treatx.health</a>
        </p>
      </div>
    </div>
  );
};

const landingStyle = {
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'radial-gradient(circle at 30% 20%, #0c1f2c 0%, #050a10 60%, #02050a 100%)',
  color: '#e8eef7',
  fontFamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  padding: '48px 20px',
  textAlign: 'center',
};
const cardStyle = {
  maxWidth: 640,
  padding: '40px 36px',
  borderRadius: 18,
  background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.08)',
  backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
};
const badgeStyle = {
  display: 'inline-block',
  padding: '6px 14px',
  borderRadius: 999,
  background: 'rgba(76,175,255,0.12)',
  color: '#7cc4ff',
  fontSize: 12,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  marginBottom: 28,
  fontWeight: 600,
};
const headlineStyle = { fontSize: 'clamp(36px, 5vw, 56px)', lineHeight: 1.05, margin: '0 0 18px', fontWeight: 700 };
const messageStyle = { fontSize: 18, lineHeight: 1.55, color: '#b8c5d6', margin: '0 0 12px' };
const etaStyle = { fontSize: 14, color: '#7a8a9d', margin: '0 0 28px' };
const primaryBtnStyle = {
  background: '#3b82f6',
  color: 'white',
  border: 'none',
  padding: '14px 28px',
  borderRadius: 999,
  fontSize: 15,
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'background 0.15s ease',
  width: '100%',
  maxWidth: 360,
  WebkitTapHighlightColor: 'rgba(59,130,246,0.35)',
  touchAction: 'manipulation',
};
// Anchor variant of primaryBtnStyle so the sign-in CTA can be a real
// <a href="#/login"> on mobile (native navigation, no JS event handlers
// to misfire on iOS Safari).
const primaryLinkBtnStyle = {
  ...{
    background: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '14px 28px',
    borderRadius: 999,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.15s ease',
    width: '100%',
    maxWidth: 360,
    WebkitTapHighlightColor: 'rgba(59,130,246,0.35)',
    touchAction: 'manipulation',
  },
  display: 'inline-block',
  textAlign: 'center',
  textDecoration: 'none',
  boxSizing: 'border-box',
};
const secondaryBtnStyle = {
  background: 'transparent',
  color: '#cdd9e8',
  border: '1px solid rgba(255,255,255,0.18)',
  padding: '13px 24px',
  borderRadius: 999,
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  width: '100%',
  maxWidth: 360,
};
const inputStyle = {
  width: '100%',
  maxWidth: 360,
  padding: '13px 18px',
  borderRadius: 12,
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.14)',
  color: '#e8eef7',
  fontSize: 16,
  fontFamily: '"JetBrains Mono", ui-monospace, monospace',
  letterSpacing: '0.06em',
  textAlign: 'center',
  outline: 'none',
};

export default SoftLaunchGate;
